<template>
    <div class="container-fluid">
        <div class="client-table-options">
            <span>Please select the table you would like to view: </span>
            
            <q-select 
                style="width: 400px"
                dense 
                clearable
                outlined
                label-color="black"
                bg-color="white"
                v-model="selectedTableOption" 
                :options="tableOptions"
                option-label="name"
                option-value="tableValue"
            />
            <q-btn :disable="selectedTableOption == ''" :loading="retrievingTableData" no-caps label="Generate table" color="primary" @click="retrieveTableData"/>
        </div>

        <div>
            <q-table
                class="client-table"
                v-if="tableLoaded"
                :loading="retrievingTableData"
                :rows="filteredTableDataRows"
                :columns="tableDataColumns"
                :rows-per-page-options="[25]"
                :filter="filter"
                :filter-method="selectedTableOption.tableValue === 'staff-hours-spent' ? undefined : filterPauaClientTable"
                @row-click="onRowClick"
                :visibleColumns="visibleColumns"
            >

                <template v-slot:top-left>
                    <div class="users-title view-user-title">
                        <q-input outlined dense debounce="300" v-model="filter" :placeholder="selectedTableOption.tableValue === 'staff-hours-spent' ? 'Search Table' : 'Name, NHI, email, phone, date of birth, ID or table details'">
                            <template v-slot:append>
                                <q-icon name="close" @click="filter = ''" class="cursor-pointer" />
                                <q-icon name="search" />
                            </template>
                        </q-input>

                        <q-select
                            v-model="visibleColumns"
                            multiple
                            outlined
                            dense
                            options-dense
                            :display-value="$q.lang.table.columns"
                            emit-value
                            map-options
                            :options="tableDataColumns"
                            option-value="name"
                            style="min-width: 150px; margin-bottom: 0;"
                        />

                        <q-toggle 
                            v-if="selectedTableOption.tableValue == 'staff-hours-spent'" 
                            class="client-select-input" 
                            label="Active Staff Only"
                            v-model="activeStaff" 
                        />

                    </div>
                </template>

                <template #header-cell="props">
                    <q-th :props="props">
                        <span>{{ props.col.label }}</span>
                        <q-tooltip v-if="props.col.tooltip">
                            {{ props.col.tooltip }}
                        </q-tooltip>
                    </q-th>
                </template>

                <template v-slot:top-right>
                    <div class="users-title view-user-title">
                        <span v-if="selectedTableOption.tableValue == 'interaction-frequency'" style="margin-inline: 2rem;">Click a client to view all interactions</span>
                        <span v-if="selectedTableOption.tableValue == 'high-risk-clients'" style="margin-inline: 2rem;">The following clients have been identified as at risk</span>
                        <span v-if="selectedTableOption.tableValue == 'staff-hours-spent'" style="margin-inline: 2rem;">Click a staff member to see hours spent</span>
                        <span v-if="selectedTableOption.tableValue == 'client-exit-timeline'" style="margin-inline: 2rem;">This table only contains exited clients. Any filters applied regarding status aren't applied.</span>
                        <q-btn no-caps fab-mini flat icon="download" @click="exportTable(getVisibleRows(filteredTableDataRows, visibleColumns), `${selectedTableOption.name}.csv`, { columns: tableDataColumns })" />

                    </div>
                </template>

                <template v-slot:body-cell="props">
                    <q-td :props="props">
                        <b v-if="(props.col.name === 'days_since_entry' && highlight(props.row).entryDays) || (props.col.name === 'days_since_last_interaction' && highlight(props.row).lastInteractionDays)">
                            {{ props.value }}
                        </b>
                        <span v-else-if="props.col.name === 'person_profile'">
                            <q-btn icon="o_person" outline size="sm" @click.stop>
                                <q-popup-proxy>
                                    <ActivePersonProfileComponent :personId="props.row.person_id" />
                                </q-popup-proxy> 
                            </q-btn>
                        </span>
                        <span v-else-if="props.col.name === 'source_left'">
                            {{ props.value?.replaceAll("_", " ") }}
                        </span>

                        <span v-else-if="props.col.name.includes('date')">
                            {{ props.value ? props.value.split('T')[0] : '' }}
                        </span>
                        <span v-else>
                            {{ props?.value }}
                        </span>
                    </q-td>
                </template>
            </q-table>
        </div>

        <ClientInteractionComponent v-if="showInteractionInformation" :clientRow="clientRow" @close="handleClose" />

        <StaffInteractionComponent v-if="showStaffInteractionInformation" :staffRow="staffRow" @close="handleClose" />
    </div>
</template>

<script>

import api from "../../services/api/api"
import notify from '@/services/util/notify';

import ActivePersonProfileComponent from "./IndividualPopupComponents/ActivePersonProfileComponent.vue";
import ClientInteractionComponent from "./IndividualPopupComponents/ClientInteractionComponent.vue";
import StaffInteractionComponent from "./IndividualPopupComponents/StaffInteractionComponent.vue";

import { exportTable, getVisibleRows } from "../../services/util/file";
import { filterPauaClientTable } from "@/services/util/pauaClientSearch";

export default ({
    name: "ClientTableParent",

    components: { ActivePersonProfileComponent, ClientInteractionComponent, StaffInteractionComponent },

    props: {
        query: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            tableOptions: [
                { name: "Interaction Frequency", tableValue: "interaction-frequency" },
                { name: "Time Since Last Interactions", tableValue: "interaction-time-since" },
                { name: "Timeline (Active)", tableValue: "active-client-timeline" },
                { name: "Exit Timeline", tableValue: "client-exit-timeline" },
                { name: "Staff Hours Spent", tableValue: "staff-hours-spent" },
                { name: "At Risk Clients", tableValue: "high-risk-clients"},
            ],
            
            exportTable: exportTable,
            getVisibleRows: getVisibleRows,

            selectedTableOption: "",

            tableLoaded: false,
            retrievingTableData: false,

            tableDataRows: [],
            filteredTableDataRows: [],
            tableDataColumns: [],
            visibleColumns: [],

            filter: null,

            clientRow: null,
            showInteractionInformation: false,

            staffRow: null,
            showStaffInteractionInformation: false,

            activeStaff: false,

            exportLoading: false,
        }
    },

    watch: {
        selectedTableOption() {
            this.tableLoaded = false;
        },

        activeStaff() {
            this.applyActiveStaffToggle();
        }
    },

    methods: {
        filterPauaClientTable,
        /**
         * Retrieve the specified table option
         */
        async retrieveTableData() {
            this.retrievingTableData = true;

            if (this.selectedTableOption == "") {
                notify.error("Please select a table option before generating table", "top")
                return;
            }

            api.client.getClientTableData(this.selectedTableOption.tableValue, this.query).then((res) => {
                this.tableDataRows = this.selectedTableOption.tableValue === 'interaction-time-since'
                    ? res.data.tableRows.map((row) => ({
                        ...row,
                        days_since_last_interaction: Number.isNaN(parseInt(row.days_since_last_interaction, 10))
                            ? row.days_since_last_interaction
                            : parseInt(row.days_since_last_interaction, 10)
                    }))
                    : res.data.tableRows;
                this.filteredTableDataRows = this.tableDataRows
                this.tableDataColumns = res.data.tableColumns;
            
                this.visibleColumns = this.tableDataColumns
                    .filter((column) => column.defaultVisibility)
                    .map((column) => column.name);
                this.tableLoaded = true
            }).catch((error) => {
                notify.error(error.message, 'top');
            }).finally(() => {
                this.retrievingTableData = false
            })
        },


        /**
         * Handles row click by calling the appropriate methods
         * @param {*} evt 
         * @param {*} row 
         */
        onRowClick(evt, row) {
            if (this.selectedTableOption.tableValue == 'interaction-frequency' || this.selectedTableOption.tableValue == 'active-client-timeline') {
                this.clientRow = row
                this.showInteractionInformation = true;
            } else if (this.selectedTableOption.tableValue == 'staff-hours-spent') {
                if (row.email) {
                    this.staffRow = row
                    this.showStaffInteractionInformation = true
                } else {
                    notify.error('Selected staff member does not have an email', 'top');
                }
            }
        },

        handleClose() {
            this.showInteractionInformation = false;
            this.showStaffInteractionInformation = false;
        },

        /**
         * Checks if the given row's service/programme/source matches the given value.
         *
         * @param {string} value - The value to be checked.
         * @param {Object} row - The row object.
         * @returns {boolean} - Returns true if the value matches any of the properties of the row object, otherwise false.
         */
        isRow(value, row) {
            const lowerValue = value.toLowerCase();
            return (
                row.service_type_id?.trim().toLowerCase() === lowerValue ||
                row.programme_id?.trim().toLowerCase() === lowerValue ||
                row.source_left?.trim().toLowerCase() === lowerValue
            );
        },

        /**
         * Determines if entry days and last interaction days match the conditions, based on the row object.
         *
         * @param {Object} row - The row object.
         * @returns {Object} - Returns an object with properties 'entryDays' and 'lastInteractionDays'.
         */
        highlight(row) {
            let entryDays = 90;
            let lastInteractionDays = 30;

            if (this.isRow("mana ake", row)) {
                entryDays = 112;
                lastInteractionDays = entryDays;
            } else if (this.isRow("pumotomoto", row)) {
                entryDays = 2000;
                lastInteractionDays = entryDays;
            } else if (this.isRow("powhiri", row)) {
                entryDays = 730;
                lastInteractionDays = entryDays;
            }

            return {
                entryDays: row.days_since_entry > entryDays,
                lastInteractionDays: row.days_since_last_interaction > lastInteractionDays,
            };
        },


        applyActiveStaffToggle() {
            if (this.activeStaff) {
                this.filteredTableDataRows = this.tableDataRows.filter((item) => item.active == 1);
            } else {
                this.filteredTableDataRows = this.tableDataRows;
            }
        }
    }
})

</script>

<style>

@import '../../assets/styles/clientDemographics.css';

</style>
